import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaImage, FaEye } from 'react-icons/fa';
import { apiService } from '../services/api';
import api from '../services/api';
import SafeImg from '../components/SafeImg';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';
import { readValidatedImageAsDataUrl } from '../utils/imageUpload';

const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    category: 'Events',
    isActive: true
  });

  const [imageFile, setImageFile] = useState(null);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      // Fetch ALL images for admin panel
      const response = await api.get('/gallery/all');
      console.log('🖼️ Fetched gallery images:', response.data);
      setImages(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setImages([
        { id: 1, title: 'Annual Function', description: 'Cultural event', imageUrl: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800', category: 'Events', isActive: true },
        { id: 2, title: 'Sports Day', description: 'Athletic events', imageUrl: 'https://images.unsplash.com/photo-1461896836934-voices60dcc7c?w=800', category: 'Sports', isActive: true }
      ]);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      alert('Please enter a title.');
      return;
    }
    if (!formData.imageUrl || !String(formData.imageUrl).trim()) {
      alert('Please paste an image link or choose a file to upload.');
      return;
    }
    try {
      if (editingId) {
        await apiService.gallery.update(editingId, formData);
        alert('✅ Gallery image updated!');
      } else {
        await apiService.gallery.create(formData);
        alert('✅ Gallery image added!');
      }
      setShowForm(false); setEditingId(null); resetForm(); setImageFile(null); fetchGallery();
    } catch (error) {
      console.error('CRUD Error:', error);
      const d = error.response?.data;
      const msg = d?.message || d?.error || (Array.isArray(d?.errors) ? d.errors.map((e) => e.message || e).join('\n') : null) || error.message;
      alert('❌ Error: ' + msg);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    try {
      const dataUrl = await readValidatedImageAsDataUrl(file);
      setFormData((prev) => ({ ...prev, imageUrl: dataUrl }));
    } catch (err) {
      alert(err.message || 'Invalid image');
      e.target.value = '';
      setImageFile(null);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ title: item.title, description: item.description, imageUrl: item.imageUrl, category: item.category, isActive: toBool(item.isActive, true) });
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this image?')) return;
    try { await apiService.gallery.delete(id); alert('✅ Image deleted successfully!'); fetchGallery(); }
    catch (error) { console.error('Delete error:', error); alert('❌ Error deleting image: ' + (error.response?.data?.message || error.message)); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', imageUrl: '', category: 'Events', isActive: true });
    setImageFile(null);
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">Gallery Management</h1>
          <button onClick={() => { setShowForm(true); setEditingId(null); resetForm(); }}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2">
            <FaPlus /> Add New Image
          </button>
        </motion.div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit Image' : 'Add New Image'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image URL (or upload below) *</label>
                  <input
                    type="text"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="https://... or leave empty if you upload a file"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste a full https link, or use &quot;Upload&quot; for a data URL (no browser URL validation on data:image).</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Or Upload Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900"
                  />
                  {formData.imageUrl && (
                    <div className="mt-2">
                      <SafeImg src={formData.imageUrl} alt="Preview" className="max-w-xs rounded-lg shadow-md" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                    <option value="Events">Events</option>
                    <option value="Sports">Sports</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Activities">Activities</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Image
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <SafeImg src={item.imageUrl} alt={item.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-blue-900">{item.title}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                <span className="inline-block bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-xs mb-3">{item.category}</span>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded flex-1"><FaEdit /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded flex-1"><FaTrash /></button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
        {images.length === 0 && <div className="text-center py-12 text-gray-500">No images found.</div>}
      </div>
    </AdminLayout>
  );
};

export default GalleryManagement;
